import { useEffect, useState, useMemo } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import CustomModal from '../../../../components/atoms/CustomModal';
import InputSelect from '../../../../components/atoms/InputSelect';
import Button from '../../../../components/atoms/Button';
import Uploader from '../../../../components/atoms/Uploader';
import {
  useCreatePromotionDefaultContentMutation,
  useUpdatePromotionDefaultContentMutation,
  useUploadPromotionDefaultContentDocumentsMutation,
} from '../../../../store/services/promotionsService';
import { PromotionDefaultContent } from '../../../../types/promotionDefaultContents';
import type { UploadFile } from 'antd/es/upload/interface';

const bannerSchemaBase = z.object({
  marketplaceScopeCode: z.string().min(1),
  priority: z.coerce.number().min(1),
  documents: z.array(z.any()).min(1),
});

type BannerFormValues = z.infer<typeof bannerSchemaBase>;

interface SetYadBannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  banner: PromotionDefaultContent | null;
}

export default function SetYadBannerModal({
  isOpen,
  onClose,
  banner,
}: SetYadBannerModalProps) {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const [createBanner, { isLoading: isCreating }] =
    useCreatePromotionDefaultContentMutation();
  const [updateBanner, { isLoading: isUpdating }] =
    useUpdatePromotionDefaultContentMutation();
  const [uploadDocuments, { isLoading: isUploading }] =
    useUploadPromotionDefaultContentDocumentsMutation();

  const bannerSchema = useMemo(() => z.object({
    marketplaceScopeCode: z.string().min(1, t('home.please-select-destination', 'Please select a destination')),
    priority: z.coerce.number().min(1, t('home.please-set-priority', 'Please set a priority')),
    documents: z.array(z.any()).min(1, t('home.please-upload-image', 'Please upload at least one image')),
  }), [t]);

  const methods = useForm<BannerFormValues>({
    resolver: zodResolver(bannerSchema),
    mode: 'onChange',
    defaultValues: {
      marketplaceScopeCode: '',
      priority: 1,
      documents: [],
    },
  });

  const { control, handleSubmit, reset, setValue, clearErrors } = methods;

  useEffect(() => {
    if (isOpen && banner) {
      const docs =
        banner.documents ||
        (banner.documentId
          ? [{ id: banner.documentId, url: banner.documentUrl }]
          : []);

      reset({
        marketplaceScopeCode: banner.marketplaceScopeCode,
        priority: banner.priority,
        documents: docs,
      });

      if (docs.length > 0) {
        setFileList(
          docs.map((doc: any) => ({
            uid: doc.id,
            name: 'Image.jpg',
            type: 'image/jpeg',
            status: 'done',
            url: doc.url,
            response: { id: doc.id },
          })) as UploadFile[]
        );
      } else {
        setFileList([]);
      }
    } else if (isOpen) {
      reset({
        marketplaceScopeCode: '',
        priority: 1,
        documents: [],
      });
      setFileList([]);
    }
  }, [isOpen, banner?.id, reset]);

  const onSubmit = async (data: BannerFormValues) => {
    try {
      // Collect document IDs from all uploaded files
      const allDocumentIds = fileList
        .map((f) => {
          const res = f.response;
          const id =
            res?.id ||
            res?.documentId ||
            (typeof res === 'string' ? res : null);
          return id || (!f.uid.startsWith('rc-upload') ? f.uid : null);
        })
        .filter(Boolean) as string[];

      const payload = {
        marketplaceScopeCode: data.marketplaceScopeCode,
        priority: data.priority,
        documentId: allDocumentIds[0],
      };

      if (banner?.id) {
        await updateBanner({ id: banner.id, body: payload }).unwrap();
        message.success(
          t('home.banner-updated', 'Banner updated successfully')
        );
      } else {
        await createBanner(payload).unwrap();
        message.success(
          t('home.banner-created', 'Banner created successfully')
        );
      }

      onClose();
    } catch (error: any) {
      message.error(error?.data?.message || t('home.failed-to-save-banner', 'Failed to save banner'));
    }
  };

  const isLoading = isCreating || isUpdating || isUploading;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('home.banner-information', 'Banner information')}
      subTitle={t(
        'home.banner-information-desc',
        'The core identity of your product listing.'
      )}
      width={700}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6 mt-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="marketplaceScopeCode"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <InputSelect
                  {...field}
                  label={t('home.destination', 'Destination')}
                  required
                  status={error ? 'error' : undefined}
                  helperText={error?.message}
                  options={[
                    {
                      label: t('home.home-page', 'Home Page'),
                      value: 'MAIN_MARKETPLACE',
                    },
                    {
                      label: t('home.category-page', 'Category Page'),
                      value: 'CATEGORY_MARKETPLACE',
                    },
                  ]}
                  placeholder={t(
                    'home.select-destination',
                    'select destination'
                  )}
                />
              )}
            />
            <Controller
              name="priority"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <InputSelect
                  {...field}
                  label={t('home.set-priority', 'Set Priority')}
                  required
                  status={error ? 'error' : undefined}
                  helperText={error?.message}
                  options={Array.from({ length: 10 }, (_, i) => ({
                    label: String(i + 1),
                    value: i + 1,
                  }))}
                  placeholder={t('home.set', 'Set')}
                />
              )}
            />
          </div>

          <Controller
            name="documents"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Uploader
                label={t('home.banner-images', 'Banner Images')}
                required
                status={error ? 'error' : undefined}
                helperText={error?.message}
                fileList={fileList}
                loading={isUploading}
                allowedTypes={['image/png', 'image/jpeg', 'image/jpg']}
                multiple={false}
                maxCount={1}
                maxFileSize={5}
                uploadDescription={t(
                  'home.upload-desc',
                  'Simply drag and drop your photo here to upload.'
                )}
                uploadHints={[
                  t(
                    'home.upload-hint-types',
                    'Accepted file types: [.jpg, .png]'
                  ),
                  t('home.upload-hint-size', 'Max file size: [5MB] 1440x400 - 3200x800 px'),
                ]}
                onChangeFile={(newFileList) => {
                  const errorMsg = t('home.invalid-banner-dimensions-error', 'Invalid image dimensions');
                  const hasDimensionError = newFileList.some(
                    (f) => f.status === 'error' && f.error?.message === errorMsg
                  );
                  const validFiles = newFileList.filter(
                    (f) =>
                      !(
                        f.status === 'error' &&
                        f.error?.message === errorMsg
                      )
                  );
                  setFileList(validFiles);
                  if (hasDimensionError && validFiles.length === 0) {
                    setValue('documents', validFiles, { shouldValidate: false });
                    clearErrors('documents');
                  } else {
                    field.onChange(validFiles);
                  }
                }}
                customRequest={async ({ file, onSuccess, onError }) => {
                  try {
                    if ((file as File).type?.startsWith('image/')) {
                      const isValid = await new Promise<boolean>((resolve) => {
                        const img = new window.Image();
                        img.src = URL.createObjectURL(file as File);
                        img.onload = () => {
                          const isWidthValid = img.width >= 1440 && img.width <= 3200;
                          const isHeightValid = img.height >= 400 && img.height <= 800;
                          if (isWidthValid && isHeightValid) {
                            resolve(true);
                          } else {
                            resolve(false);
                          }
                        };
                        img.onerror = () => resolve(false);
                      });

                      if (!isValid) {
                        message.error(
                          t(
                            'home.invalid-banner-dimensions',
                            'Image dimensions must be between 1440x400 and 3200x800 px'
                          )
                        );
                        setFileList((prev) => {
                          const revertedList = prev.filter(
                            (f) => f.uid !== (file as any).uid
                          );
                          setValue('documents', revertedList, { shouldValidate: false });
                          clearErrors('documents');
                          return revertedList;
                        });
                        onError?.(new Error(t('home.invalid-banner-dimensions-error', 'Invalid image dimensions')));
                        return;
                      }
                    }

                    const formData = new FormData();
                    formData.append('file', file as File);
                    const uploadRes = await uploadDocuments(formData).unwrap();

                    const payloadData = Array.isArray(uploadRes.payload)
                      ? uploadRes.payload[0]
                      : uploadRes.payload;

                    if (uploadRes.success && payloadData) {
                      onSuccess?.(payloadData);
                    } else {
                      onError?.(new Error(t('home.upload-failed', 'Upload failed')));
                    }
                  } catch (error) {
                    onError?.(error as any);
                  }
                }}
              />
            )}
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button variant="outline" type="button" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button
              variant="dark"
              type="submit"
              loading={isLoading}
              disabled={isLoading}
            >
              {t('save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </CustomModal>
  );
}
